import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import RepairListModal from "@/components/dashboard/RepairListModal";
import {
  Users, ArrowUpRight, CalendarDays,
  UserPlus, TrendingUp, Clock, Wrench, PackageCheck, Inbox,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import CalendarView from "@/pages/CalendarView";
import PromisedBoard from "@/components/dashboard/PromisedBoard";


// Sparkline decorativa (SVG estático — indica tendencia visual).
function Sparkline({ color, d }) {
  return (
    <svg width="70" height="24" viewBox="0 0 70 24" className="absolute right-4 bottom-4 opacity-60 pointer-events-none" fill="none">
      <path d={d} stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function KpiCard({ icon: Icon, label, value, delta, tone, spark, testid, onClick }) {
  const toneMap = {
    sky:   { tile: "ds-ic-tile sky",    spark: "#2F6FED" },
    amber: { tile: "ds-ic-tile amber",  spark: "#D68A00" },
    brand: { tile: "ds-ic-tile",        spark: "#6E8B3D" },
    violet:{ tile: "ds-ic-tile violet", spark: "#6D4AA6" },
  };
  const t = toneMap[tone] || toneMap.brand;
  return (
    <button type="button" onClick={onClick} className="ds-card ds-lift relative overflow-hidden p-5 w-full text-left cursor-pointer hover:border-[#6E8B3D] focus:outline-none focus:ring-2 focus:ring-[#6E8B3D]/40" data-testid={testid} title={`Ver todas las reparaciones en ${label}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="font-mono text-[10.5px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">
          {label}
        </div>
        <span className={t.tile}><Icon size={15} /></span>
      </div>
      <div className="font-display font-black text-[32px] leading-none mt-2 tracking-tight">{value}</div>
      {delta && <div className="text-[11.5px] font-semibold mt-2 text-[#3B7A2E]">{delta}</div>}
      {spark && <Sparkline color={t.spark} d={spark}/>}
    </button>
  );
}

const KPI_TONES = {
  recibido: { head: "text-[#2F6FED]", chip: "bg-[#EAF1FF] text-[#2F6FED] border-[#BBD0FA]" },
  en_diagnostico: { head: "text-[#8A6100]", chip: "bg-[#FFF7E0] text-[#8A6100] border-[#F1DC9A]" },
  en_reparacion: { head: "text-[#3F5222]", chip: "bg-[#EFF3E6] text-[#3F5222] border-[#CFDBB3]" },
  listo_para_entregar: { head: "text-[#6D4AA6]", chip: "bg-[#F1EBFA] text-[#6D4AA6] border-[#D7C8EE]" },
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [statusModal, setStatusModal] = useState(null); // { status, label, icon, items }
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  // Iter 164 — KPI clicable → modal con todas las reparaciones de ese estado.
  const openStatus = async (status, label, icon) => {
    setStatusModal({ status, label, icon, items: null });
    try {
      const r = await api.get("/dashboard/status-repairs", { params: { status } });
      setStatusModal({ status, label, icon, items: r.data.items || [] });
    } catch {
      toast.error("No se pudo cargar el listado");
      setStatusModal(null);
    }
  };

  const byStatus = stats?.by_status || {};

  return (
    <div className="px-6 lg:px-8 py-6 lg:py-8 max-w-[1500px] mx-auto">
      {/* HEADER */}
      <header className="flex items-end justify-between pb-6 gap-4 flex-wrap border-b border-dashed border-[#D5D7CE]">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
            <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Panel de control
          </div>
          <h1 className="font-display text-4xl lg:text-[38px] font-black mt-2 tracking-tight" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-sm text-[#4B5158] mt-1">
            Vista general de tu taller en tiempo real.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/customers/new"
            className="ds-btn"
            data-testid="dashboard-new-customer"
          >
            <UserPlus size={15} />
            Nuevo cliente
          </Link>
          <Link
            to="/repairs"
            className="ds-btn primary"
            data-testid="dashboard-new-repair"
          >
            Nueva reparación
            <ArrowUpRight size={15} />
          </Link>
        </div>
      </header>

      {/* KPI STRIP */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiCard
          icon={Inbox} label="Recibido" tone="sky"
          value={byStatus.recibido ?? "—"}
          spark="M2 18 L12 14 L22 16 L32 10 L42 12 L52 6 L62 9 L68 4"
          testid="kpi-recibido"
          onClick={() => openStatus("recibido", "Recibido", Inbox)}
        />
        <KpiCard
          icon={Search} label="En diagnóstico" tone="amber"
          value={byStatus.en_diagnostico ?? "—"}
          spark="M2 20 L12 18 L22 15 L32 12 L42 10 L52 14 L62 8 L68 6"
          testid="kpi-diagnostico"
          onClick={() => openStatus("en_diagnostico", "En diagnóstico", Search)}
        />
        <KpiCard
          icon={Wrench} label="En reparación" tone="brand"
          value={byStatus.en_reparacion ?? "—"}
          spark="M2 14 L12 12 L22 10 L32 8 L42 6 L52 10 L62 8 L68 4"
          testid="kpi-reparacion"
          onClick={() => openStatus("en_reparacion", "En reparación", Wrench)}
        />
        <KpiCard
          icon={PackageCheck} label="Listo para entregar" tone="violet"
          value={byStatus.listo_para_entregar ?? "—"}
          spark="M2 20 L12 18 L22 14 L32 12 L42 8 L52 10 L62 6 L68 3"
          testid="kpi-listo"
          onClick={() => openStatus("listo_para_entregar", "Listo para entregar", PackageCheck)}
        />
      </section>

      {/* Iter 163 — Tablero de prioridad por Fecha estimada */}
      <PromisedBoard />


      {statusModal && (
        <RepairListModal
          title={statusModal.label}
          icon={statusModal.icon}
          tone={KPI_TONES[statusModal.status]}
          testid="kpi-status-modal"
          subtitle={statusModal.items ? `${statusModal.items.length} reparación(es) en este estado. Pulsa una para abrir su ficha.` : "Cargando…"}
          items={statusModal.items || []}
          loading={statusModal.items === null}
          onClose={() => setStatusModal(null)}
          onOpen={(it) => { setStatusModal(null); navigate(`/repairs/${it.id}`); }}
        />
      )}

      {/* Widget Calendario */}
      <section className="mt-8">
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display text-[22px] font-black tracking-tight flex items-center gap-3">
            <span className="ds-ic-tile" style={{ width: 32, height: 32, borderRadius: 9 }}>
              <CalendarDays size={16}/>
            </span>
            Agenda del día · 10:00 → 20:00
          </h2>
          <Link
            to="/calendar"
            className="text-xs font-mono uppercase tracking-wider text-[#4B5158] hover:text-[#0F1210] inline-flex items-center gap-1"
          >
            Vista completa <ArrowUpRight size={12} />
          </Link>
        </div>
        <div className="ds-card p-2">
          <CalendarView embedded dashboardMode />
        </div>
      </section>
    </div>
  );
}
